type FormToJSONOptions = {
	omitEmpty?: boolean;
	arrayFields?: string[];
};

/**
 * Takes a FormData object and converts it to a JSON (assuming only basic inputs).
 * Fields that share the same name attribute will be coalesced into an array under
 * the same key.
 *
 * NOTE: We may need to rework this for file uploads at a different date, though
 * at time of writing I'm not entirely sure what form that rework would take.
 * @param formData a FormData object
 * @param options optional FormToJSONOptions object for additional customization
 * @returns a JSON representation of the data held in the FormData object.
 */
const formDataToJSON = (
	formData: FormData,
	{omitEmpty, arrayFields}: FormToJSONOptions = {}
) => {
	const submissionData: any = {};
	for (const [key, value] of formData.entries()) {
		if (omitEmpty && !value) {
			continue;
		}

		// add key to data object if it doesn't exist
		if (!submissionData.hasOwnProperty(key)) {
			if (arrayFields?.includes(key)) {
				submissionData[key] = [value];
				continue;
			}
			submissionData[key] = value;
			continue;
		}

		// append the value to existing data otherwise
		// REVIEW: does lodash have a one-liner for this?
		const existingData = submissionData[key];
		if (Array.isArray(existingData)) {
			submissionData[key] = [value, ...existingData];
		} else {
			submissionData[key] = [value, existingData];
		}
	}
	return submissionData;
};

/**
 * Takes a string input and strips non-numeric digits and leading 1's
 * @param s The string from which to extract a phone number
 * @returns The extracted phone number as a string
 */
const extractPhoneNumber = (s: string) => {
	if (!s) {
		return "";
	}
	let digits = s.replace(/\D/g, "");
	if (digits.startsWith("1")) {
		digits = digits.substring(1);
	}

	// Must be 10 digits, first and fourth digits must be 2-9
	if (!/^[2-9][0-9]{2}[2-9][0-9]{2}[0-9]{4}$/.test(digits)) {
		return "";
	}

	return digits;
};

/**
 * Takes a string input (phoneNumber) and runs basic validation over the
 * input (stripping non-digit characters and a leading 1, the number should be 10
 * characters long)
 * @param phoneNumber The string to run validation over.
 * @returns false if the string does not contain a "valid" phone number.
 *          Otherwise returns true.
 */
const isValidPhoneNumber = (phoneNumber: string) => {
	const digits = extractPhoneNumber(phoneNumber);
	return !!digits;
};

/**
 * Takes a string input (phoneNumber) and returns true if it contains 10 numeric
 * characters in sequence without a leading 1, or a string error otherwise.
 * @param phoneNumber The string to validate
 * @returns true if it contains 10 numeric characters in sequence without a
 *          leading 1, or a string error otherwise.
 */
const validatePhoneNumber = (phoneNumber: string) => {
	// phone number is not required.  We treat the empty string as valid.
	if (!phoneNumber) {
		return true;
	}

	if (!isValidPhoneNumber(phoneNumber)) {
		return "Please enter a valid 10-digit phone number";
	}

	return true;
};

/**
 * Takes a string input (phoneNumber) and formats it into the shape DDD-DDD-DDDD
 * (parallels the behaviour of Academy::normalizePhone on the legacy side)
 *
 * @param phoneNumber The string to format
 * @returns an empty string the phone number is invalid.  Otherwise returns the
 *          number in the form DDD-DDD-DDDD
 */
const formatPhoneNumber = (phoneNumber: string) => {
	const digits = extractPhoneNumber(phoneNumber);
	if (!digits) {
		return "";
	}
	const partitions = phoneNumber.match(/^(\d{3})(\d{3})(\d{4})$/);

	if (!partitions || partitions.length !== 3) {
		return "";
	}

	return `${partitions[0]}-${partitions[1]}-${partitions[2]}`;
};

export {
	formDataToJSON,
	extractPhoneNumber,
	isValidPhoneNumber,
	validatePhoneNumber,
	formatPhoneNumber,
};
